// AuthContext.js - Provee contexto de autenticación con expiración dura (12h)
import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import "../App.css"; // Importa estilos globales

const AuthContext = createContext();

// Duración de sesión (12 horas)
const SESSION_MS = 12 * 60 * 60 * 1000;
const STORAGE_KEY = "hidrometrics_auth";

const readStoredSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.user || !parsed?.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStoredSession = (user, expiresAt) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ user, expiresAt }));
  } catch {
    /* ignore */
  }
};

const clearStoredSession = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const logoutTimerRef = useRef(null);

  const scheduleLogoutAt = useCallback((expiresAt) => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

    const msRemaining = Math.max(0, expiresAt - Date.now());
    logoutTimerRef.current = setTimeout(() => {
      // Seguridad: revalida al disparar
      const stored = readStoredSession();
      if (!stored || Date.now() >= stored.expiresAt) {
        logout();
      }
    }, msRemaining);
  }, []);

  const login = useCallback((userData) => {
    const expiresAt = Date.now() + SESSION_MS; // 12h duras
    setUser(userData);
    writeStoredSession(userData, expiresAt);
    scheduleLogoutAt(expiresAt);
  }, [scheduleLogoutAt]);

  const logout = useCallback(() => {
    setUser(null);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    clearStoredSession();
  }, []);

  // Al montar: carga sesión si sigue vigente
  useEffect(() => {
    const stored = readStoredSession();
    if (stored && stored.user && stored.expiresAt > Date.now()) {
      setUser(stored.user);
      scheduleLogoutAt(stored.expiresAt);
    } else {
      clearStoredSession();
    }
    // Limpieza al desmontar
    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [scheduleLogoutAt]);

  // Cierra sesión si la pestaña vuelve a estar visible y la sesión ya expiró
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        const stored = readStoredSession();
        if (!stored || Date.now() >= (stored?.expiresAt || 0)) {
          logout();
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [logout]);

  // Sincroniza logout/login entre pestañas
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      const stored = readStoredSession();
      // Si no hay sesión o expiró -> logout local
      if (!stored || Date.now() >= (stored.expiresAt || 0)) {
        logout();
        return;
      }
      // Si hay sesión válida en otra pestaña -> aplícala localmente
      setUser(stored.user);
      scheduleLogoutAt(stored.expiresAt);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [logout, scheduleLogoutAt]);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
